import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** Translation keys for fabric item detection */
const FABRIC_PROPERTY_PATTERNS = {
  typeKeys: ['typ', 'type'],
  fabricValues: ['stoffl', 'fabric', 'länge', 'length', 'meterware'],
  meterValues: ['100cm', '1m', '1 m', 'meter'],
  lengthKeys: ['länge', 'length'],
};

/** Upper bound for syncing the cart before checkout, so a hanging request never blocks the checkout */
const CHECKOUT_SYNC_TIMEOUT = 8000;

/** @type {Set<CartItemsComponent>} All connected cart items components (cart page and cart drawer) */
const cartItemsInstances = new Set();

/** @type {Set<Promise<unknown>>} Cart requests that are still in flight */
const pendingCartRequests = new Set();

/** @type {Promise<void> | null} Running fabric length reconciliation */
let fabricReconcilePromise = null;

/**
 * Tracks a cart request so the checkout can wait for it.
 * @template T
 * @param {Promise<T>} request
 * @returns {Promise<T>}
 */
function trackCartRequest(request) {
  pendingCartRequests.add(request);
  request.finally(() => pendingCartRequests.delete(request)).catch(() => {});
  return request;
}

/**
 * Calculates the length in cm based on quantity.
 * @param {number} quantity - The cart quantity.
 * @param {boolean} isMeterItem - Whether it's 1m mode.
 * @returns {number} The length in cm.
 */
function calculateFabricLength(quantity, isMeterItem) {
  // 1m mode: quantity 1 = 100cm, 50cm mode: quantity 1 = 50cm
  return quantity * (isMeterItem ? 100 : 50);
}

/**
 * Checks if a cart item is a fabric item based on its line item properties.
 * @param {Record<string, string> | null | undefined} properties - The item properties.
 * @returns {{isFabricItem: boolean, isMeterItem: boolean, lengthPropertyKey: string | null}}
 */
function detectFabricItem(properties) {
  let isFabricItem = false;
  let isMeterItem = false;
  let lengthPropertyKey = null;

  for (const [key, value] of Object.entries(properties || {})) {
    const keyLower = key.toLowerCase();
    const valueLower = String(value).toLowerCase();

    // Check for type property
    if (FABRIC_PROPERTY_PATTERNS.typeKeys.some((k) => keyLower === k)) {
      // Check if it's a fabric length item
      if (FABRIC_PROPERTY_PATTERNS.fabricValues.some((v) => valueLower.includes(v))) {
        isFabricItem = true;
      }
      // Check if it's 1m mode - but 50cm takes priority
      // "Meterware ab 50cm" should NOT be detected as 1m mode
      const is50cmMode = valueLower.includes('50cm') || valueLower.includes('0,5');
      if (!is50cmMode && FABRIC_PROPERTY_PATTERNS.meterValues.some((v) => valueLower.includes(v))) {
        isMeterItem = true;
      }
    }

    // Find the length property key - if it exists with a value in "cm", it's a fabric item
    if (FABRIC_PROPERTY_PATTERNS.lengthKeys.some((k) => keyLower === k)) {
      lengthPropertyKey = key;
      if (valueLower.includes('cm')) {
        isFabricItem = true;
      }
    }
  }

  return { isFabricItem, isMeterItem, lengthPropertyKey };
}

/**
 * Returns the properties as a plain object.
 * Liquid's `item.properties | json` renders an array of [key, value] pairs, not an object.
 * Numeric keys are dropped: they are leftovers from spreading that array into an object.
 * @param {Record<string, string> | Array<[string, string]> | null | undefined} properties
 * @returns {Record<string, string>}
 */
function normalizeProperties(properties) {
  const entries = Array.isArray(properties)
    ? properties.filter((entry) => Array.isArray(entry) && entry.length === 2)
    : Object.entries(properties || {});

  return Object.fromEntries(entries.filter(([key]) => !/^\d+$/.test(String(key))));
}

/**
 * Builds the line item properties with the length matching the quantity.
 * Returns null for non-fabric items. The fabric mode from the product metafield
 * (rendered as data-fabric-mode) takes priority over the line item properties.
 * @param {Record<string, string> | null | undefined} properties - The current line item properties.
 * @param {number} quantity - The cart quantity.
 * @param {string | undefined} fabricMode - '50cm', '1m' or undefined.
 * @returns {Record<string, string> | null}
 */
function buildFabricProperties(properties, quantity, fabricMode) {
  properties = normalizeProperties(properties);

  let { isFabricItem, isMeterItem, lengthPropertyKey } = detectFabricItem(properties);

  if (fabricMode) {
    isFabricItem = true;
    isMeterItem = fabricMode === '1m';
  }

  if (!isFabricItem || quantity <= 0) return null;

  return {
    ...(properties || {}),
    [lengthPropertyKey || 'Länge']: `${calculateFabricLength(quantity, isMeterItem)} cm`,
  };
}

/**
 * Finds the fabric mode of a product from any rendered cart row.
 * @param {number} productId
 * @returns {string | undefined}
 */
function getFabricModeForProduct(productId) {
  const row = /** @type {HTMLElement | null} */ (
    document.querySelector(`[ref="cartItemRows[]"][data-product-id="${productId}"]`)
  );
  return row?.dataset.fabricMode;
}

/**
 * Makes sure the "Länge" property of every fabric line matches its quantity on the server.
 * Catches lines that got out of sync on paths the cart UI does not control,
 * e.g. adding the same fabric again on the product page (Shopify merges the lines).
 * @returns {Promise<void>}
 */
function reconcileFabricLines() {
  if (fabricReconcilePromise) return fabricReconcilePromise;

  fabricReconcilePromise = (async () => {
    let cart = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then((r) =>
      r.json()
    );
    let changed = false;

    // Fix one line per request and continue with the returned cart, because changing
    // properties gives the line a new key. The limit guards against endless loops.
    for (let attempt = 0; attempt < 20; attempt++) {
      /** @type {Array<{product_id: number, quantity: number, properties: Record<string, string>}>} */
      const items = cart.items || [];
      const index = items.findIndex((item) => {
        const expected = buildFabricProperties(item.properties, item.quantity, getFabricModeForProduct(item.product_id));
        if (!expected) return false;
        const current = item.properties || {};
        return (
          Object.keys(current).length !== Object.keys(expected).length ||
          Object.keys(expected).some((key) => expected[key] !== current[key])
        );
      });

      if (index === -1) break;

      const item = items[index];
      const response = await fetch(
        `${Theme.routes.cart_change_url}`,
        fetchConfig('json', {
          body: JSON.stringify({
            line: index + 1,
            quantity: item.quantity,
            properties: buildFabricProperties(item.properties, item.quantity, getFabricModeForProduct(item.product_id)),
          }),
        })
      );

      if (!response.ok) throw new Error(`Failed to update fabric length (${response.status})`);

      cart = await response.json();
      changed = true;
    }

    if (changed) {
      await Promise.all(
        [...cartItemsInstances].map((component) =>
          sectionRenderer.renderSection(component.sectionId, { cache: false })
        )
      );
    }
  })().finally(() => {
    fabricReconcilePromise = null;
  });

  return fabricReconcilePromise;
}

/**
 * Applies pending quantity changes, waits for running cart requests and fixes fabric lengths.
 * @returns {Promise<void>}
 */
async function syncCartBeforeCheckout() {
  for (const component of cartItemsInstances) {
    component.flushPendingQuantityChange();
  }

  await Promise.allSettled([...pendingCartRequests]);
  await reconcileFabricLines();
}

/**
 * The checkout button submits #cart-form with updates[] quantities. Shopify then applies the
 * quantities server-side without touching the "Länge" property, so a quantity change that was
 * still debounced or in flight ended up in the order with the old length.
 * Intercept the submit, sync the cart first and go to checkout afterwards.
 */
document.addEventListener('submit', async (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || form.id !== 'cart-form') return;

  event.preventDefault();

  const submitter = event.submitter;
  const goToCheckout = submitter?.getAttribute('name') === 'checkout';

  if (submitter instanceof HTMLButtonElement) submitter.disabled = true;

  try {
    await Promise.race([
      syncCartBeforeCheckout(),
      new Promise((resolve) => setTimeout(resolve, CHECKOUT_SYNC_TIMEOUT)),
    ]);
  } catch (error) {
    console.error('Failed to sync cart before checkout:', error);
  }

  // Re-enable before leaving, otherwise the button stays disabled when the page comes back from the bfcache
  if (submitter instanceof HTMLButtonElement) submitter.disabled = false;

  if (goToCheckout) {
    // Keeps a locale prefix, e.g. /en/cart -> /en/checkout
    window.location.href = Theme.routes.cart_url.replace(/cart$/, 'checkout');
  }
});

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce((/** @type {QuantitySelectorUpdateEvent} */ event) => this.#onQuantityChange(event), 300);

  /** @type {QuantitySelectorUpdateEvent | null} Quantity change that is still waiting for the debounce */
  #pendingQuantityEvent = null;

  connectedCallback() {
    super.connectedCallback();

    cartItemsInstances.add(this);

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#queueQuantityChange);

    this.#reconcileFabricLines();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    cartItemsInstances.delete(this);

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#queueQuantityChange);
  }

  /**
   * Remembers the quantity change so the checkout can apply it before the debounce fires.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #queueQuantityChange = (event) => {
    this.#pendingQuantityEvent = event;
    this.#debouncedOnChange(event);
  };

  /**
   * Applies a debounced quantity change right away.
   */
  flushPendingQuantityChange() {
    const event = this.#pendingQuantityEvent;
    if (!event) return;

    this.#debouncedOnChange.cancel();
    this.#onQuantityChange(event);
  }

  /**
   * Fixes fabric lines whose length does not match their quantity.
   */
  #reconcileFabricLines() {
    // Query the DOM directly: refs are refreshed asynchronously after a morph
    const rows = /** @type {NodeListOf<HTMLElement>} */ (this.querySelectorAll('[ref="cartItemRows[]"]'));
    if (![...rows].some((row) => row.dataset.fabricMode || row.dataset.properties?.includes('cm'))) return;

    trackCartRequest(reconcileFabricLines()).catch((error) => {
      console.error('Failed to reconcile fabric lengths:', error);
    });
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    this.#pendingQuantityEvent = null;

    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    // Send quantity and length in one request, so the cart never holds a quantity with an outdated length
    const fabricProperties = this.#getFabricProperties(line, quantity);

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      ...(fabricProperties && { properties: fabricProperties }),
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    const request = fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then(async (responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });

    trackCartRequest(request);
  }

  /**
   * Returns the line item properties with the length for the new quantity, or null for non-fabric items.
   * @param {number} line - The line.
   * @param {number} quantity - The new quantity.
   * @returns {Record<string, string> | null}
   */
  #getFabricProperties(line, quantity) {
    const row =
      this.refs.cartItemRows?.find((cartItemRow) => cartItemRow.dataset.line === String(line)) ??
      this.refs.cartItemRows?.[line - 1];
    if (!row?.dataset.properties) return null;

    /** @type {Record<string, string>} */
    let properties = {};
    try {
      properties = normalizeProperties(JSON.parse(row.dataset.properties));
    } catch (error) {
      return null;
    }

    return buildFabricProperties(properties, quantity, row.dataset.fabricMode);
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }

    // Adding the same fabric again merges the lines and keeps the old length
    this.#reconcileFabricLines();
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(
        `quantity-selector-component[data-variant-id="${variantId}"], cart-quantity-selector-component[data-variant-id="${variantId}"]`
      );

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    const cartQuantitySelectors = document.querySelectorAll('cart-quantity-selector-component');
    for (const selector of cartQuantitySelectors) {
      if ('updateButtonStates' in selector && typeof selector.updateButtonStates === 'function') {
        selector.updateButtonStates();
      }
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
